<div class="onboarding-container" data-clarity-mask="true">
  <!-- Success Page - Shown after registration while provisioning happens in background -->
  <div class="success-page" *ngIf="showSuccessPage">
    <div class="success-container">
      <div class="success-icon-container">
        <div class="success-icon" [class.complete]="isPortalReady" [class.failed]="isProvisioningFailed">
          <mat-icon *ngIf="!isPortalReady && !isProvisioningFailed">business</mat-icon>
          <mat-icon *ngIf="isPortalReady">check_circle</mat-icon>
          <mat-icon *ngIf="isProvisioningFailed">error</mat-icon>
        </div>
      </div>

      <!-- Provisioning In Progress -->
      <ng-container *ngIf="!isPortalReady && !isProvisioningFailed">
        <h1 class="success-title">Your organization is created</h1>
        <p class="success-subtitle">The rest of setup is running in the background, so you can leave this page safely.</p>

        <div class="setup-status" aria-live="polite">
          <div class="status-icon">
            <div class="pulse-ring"></div>
            <mat-icon>settings</mat-icon>
          </div>
          <div class="status-content">
            <h3>{{ setupStatusTitle }}</h3>
            <p>{{ setupStatusDescription }}</p>
            <p class="can-leave-note">We'll email a verification code to <strong>{{ getAdminEmail() }}</strong>. You can leave this page.</p>
          </div>
        </div>

        <div class="setup-progress" aria-label="Portal setup progress">
          <div class="setup-step" *ngFor="let step of setupSteps" [class.complete]="step.state === 'complete'" [class.current]="step.state === 'current'" [class.pending]="step.state === 'pending'" [class.error]="step.state === 'error'">
            <div class="setup-step-icon">
              <mat-icon>{{ getSetupStepIcon(step.state) }}</mat-icon>
            </div>
            <div class="setup-step-copy">
              <h4>{{ step.title }}</h4>
              <p>{{ step.description }}</p>
            </div>
          </div>
        </div>
      </ng-container>

      <!-- Portal Ready -->
      <ng-container *ngIf="isPortalReady">
        <h1 class="success-title">Your portal is ready</h1>
        <p class="success-subtitle">Everything is set up and ready to go.</p>

        <div class="portal-ready-actions">
          <button mat-flat-button color="primary" class="go-to-portal-btn" (click)="goToPortal()">
            <mat-icon>launch</mat-icon>
            Go to Your Portal
          </button>
          <p class="portal-url">{{ provisioningStatus?.portalUrl || getPortalUrl() }}</p>
        </div>
      </ng-container>

      <!-- Provisioning Failed -->
      <ng-container *ngIf="isProvisioningFailed">
        <h1 class="success-title error-title">Setup needs attention</h1>
        <p class="success-subtitle">Your organization was created, but we could not finish portal setup automatically.</p>

        <div class="error-notice">
          <mat-icon>warning</mat-icon>
          <div class="error-content">
            <p>{{ provisioningStatus?.error || 'Some features may not be available immediately.' }}</p>
            <p>You can still access your portal, or retry provisioning from the portal once you sign in.</p>
          </div>
        </div>

        <div class="setup-progress" aria-label="Portal setup progress">
          <div class="setup-step" *ngFor="let step of setupSteps" [class.complete]="step.state === 'complete'" [class.current]="step.state === 'current'" [class.pending]="step.state === 'pending'" [class.error]="step.state === 'error'">
            <div class="setup-step-icon">
              <mat-icon>{{ getSetupStepIcon(step.state) }}</mat-icon>
            </div>
            <div class="setup-step-copy">
              <h4>{{ step.title }}</h4>
              <p>{{ step.description }}</p>
            </div>
          </div>
        </div>

        <div class="portal-ready-actions">
          <button mat-flat-button color="primary" class="go-to-portal-btn" (click)="goToPortal()">
            <mat-icon>launch</mat-icon>
            Go to Your Portal Anyway
          </button>
        </div>
      </ng-container>

      <div class="email-notice" *ngIf="!isPortalReady">
        <mat-icon>email</mat-icon>
        <div class="email-content">
          <h4>You do not need to stay on this page</h4>
          <p>Check <strong>{{ getAdminEmail() }}</strong> for an email verification code, then open your portal once setup finishes.</p>
        </div>
      </div>

      <div class="email-notice sent" *ngIf="isPortalReady">
        <mat-icon>mark_email_read</mat-icon>
        <div class="email-content">
          <h4>Welcome email sent!</h4>
          <p>Check your inbox at <strong>{{ getAdminEmail() }}</strong> for a verification code, then sign in to your portal.</p>
        </div>
      </div>

      <div class="next-steps" *ngIf="!isPortalReady">
        <h4>What Happens Next?</h4>
        <ul class="next-steps-list">
          <li>
            <mat-icon class="done">check_circle</mat-icon>
            <span class="done">Your organization record is already created.</span>
          </li>
          <li>
            <mat-icon>language</mat-icon>
            <span>Your portal will live at {{ provisioningStatus?.portalUrl || getPortalUrl() }}.</span>
          </li>
          <li>
            <mat-icon>mail_outline</mat-icon>
            <span>Check <strong>{{ getAdminEmail() }}</strong> for a verification code, then sign in after setup finishes.</span>
          </li>
        </ul>
      </div>

      <div class="next-steps" *ngIf="isPortalReady">
        <h4>What's Next?</h4>
        <ul class="next-steps-list">
          <li>
            <mat-icon class="done">check_circle</mat-icon>
            <span class="done">Open your portal, verify your email, and sign in with the credentials you just created.</span>
          </li>
          <li>
            <mat-icon>palette</mat-icon>
            <span>Customize your settings and branding.</span>
          </li>
          <li>
            <mat-icon>group_add</mat-icon>
            <span>Invite your first members.</span>
          </li>
        </ul>
      </div>

      <div class="support-section">
        <p>Questions? Contact us at <a href="mailto:support@membertrace.com">support&#64;membertrace.com</a></p>
      </div>
    </div>
  </div>

  <div class="container-fluid" *ngIf="!showSuccessPage">
    <div class="row g-0">
      <!-- Left Sidebar: Progress Steps (Light Theme) -->
      <div class="col-lg-4 sidebar-container">
        <div class="sidebar-content">
          <!-- Header Section -->
          <div class="header-section">
            <h1 class="headline">Get Started</h1>
            <p class="subheadline">Create your account in 2 minutes</p>
          </div>

          <!-- Vertical Progress Steps -->
          <div class="progress-steps">
            <div class="progress-step" [class.active]="stepper.selectedIndex === 0" [class.completed]="stepper.selectedIndex > 0">
              <div class="step-indicator">
                <div class="step-circle">
                  <mat-icon *ngIf="stepper.selectedIndex > 0">check</mat-icon>
                  <span *ngIf="stepper.selectedIndex <= 0">1</span>
                </div>
                <div class="step-line"></div>
              </div>
              <div class="step-content">
                <h3 class="step-title">Choose Package</h3>
                <p class="step-desc">Society or Growth</p>
              </div>
            </div>

            <div class="progress-step" [class.active]="stepper.selectedIndex === 1" [class.completed]="stepper.selectedIndex > 1">
              <div class="step-indicator">
                <div class="step-circle">
                  <mat-icon *ngIf="stepper.selectedIndex > 1">check</mat-icon>
                  <span *ngIf="stepper.selectedIndex <= 1">2</span>
                </div>
                <div class="step-line"></div>
              </div>
              <div class="step-content">
                <h3 class="step-title">Account Setup</h3>
                <p class="step-desc">Organization and admin details</p>
              </div>
            </div>

            <div class="progress-step" [class.active]="stepper.selectedIndex === 2" [class.completed]="stepper.selectedIndex > 2">
              <div class="step-indicator">
                <div class="step-circle">
                  <mat-icon *ngIf="stepper.selectedIndex > 2">check</mat-icon>
                  <span *ngIf="stepper.selectedIndex <= 2">3</span>
                </div>
                <div class="step-line"></div>
              </div>
              <div class="step-content">
                <h3 class="step-title">
                  Branding
                  <span class="optional-badge">Optional</span>
                </h3>
                <p class="step-desc">Customize your portal</p>
              </div>
            </div>

            <div class="progress-step" [class.active]="stepper.selectedIndex === 3" [class.completed]="stepper.selectedIndex > 3">
              <div class="step-indicator">
                <div class="step-circle">
                  <mat-icon *ngIf="stepper.selectedIndex > 3">check</mat-icon>
                  <span *ngIf="stepper.selectedIndex <= 3">4</span>
                </div>
              </div>
              <div class="step-content">
                <h3 class="step-title">
                  Payment
                  <span class="optional-badge" *ngIf="!requiresPaymentMethod">Optional</span>
                </h3>
                <p class="step-desc">{{ requiresPaymentMethod ? 'Card on file; not charged today' : 'Start your 90-day trial' }}</p>
              </div>
            </div>
          </div>

          <!-- Trust Indicators -->
          <div class="trust-section">
            <h4 class="trust-title">Secure & Trusted</h4>
            <div class="trust-item">
              <mat-icon>lock</mat-icon>
              <span>Encrypted in transit and at rest</span>
            </div>
            <div class="trust-item">
              <mat-icon>credit_card</mat-icon>
              <span>Card details handled by Stripe</span>
            </div>
            <div class="trust-item">
              <mat-icon>event_available</mat-icon>
              <span>90-day free trial</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Content: Form -->
      <div class="col-lg-8 form-container-wrapper">
        <div class="form-content">
          <mat-vertical-stepper [linear]="true" #stepper class="custom-stepper">

            <!-- Step 1: Package -->
            <mat-step [stepControl]="packageInformationForm" [editable]="true">
              <ng-template matStepLabel></ng-template>
              <div class="step-form">
                <div class="form-header">
                  <h2 class="form-title">Choose Your Package</h2>
                  <p class="form-description">Pick the package that fits today. You can change packages later with our team if needed.</p>
                </div>

                <app-package-select-form></app-package-select-form>

                <div class="step-actions">
                  <button mat-flat-button color="primary" matStepperNext class="btn-next" [disabled]="!isPackageStepValid()">
                    Continue
                    <mat-icon>arrow_forward</mat-icon>
                  </button>
                </div>
              </div>
            </mat-step>

            <!-- Step 2: Organization & Admin Setup -->
            <mat-step [stepControl]="organizationInformationForm" [editable]="true">
              <ng-template matStepLabel></ng-template>
              <div class="step-form">
                <div class="form-header">
                  <h2 class="form-title">Create Your Account</h2>
                  <p class="form-description">Set up your organization and administrator account for {{ selectedPackage }}.</p>
                </div>

                <app-organization-info-form></app-organization-info-form>

                <!-- Bot check + email verification: nothing is created until the emailed code is confirmed -->
                <div class="email-verification" aria-live="polite">
                  <ng-container *ngIf="!emailVerified && !verificationId">
                    <p class="email-verification-hint">
                      We'll email a 6-digit code to confirm your address before we create your organization.
                    </p>
                    <app-turnstile-widget (tokenChange)="onTurnstileToken($event)"></app-turnstile-widget>
                  </ng-container>

                  <div class="email-verification-code" *ngIf="!emailVerified && verificationId">
                    <p>
                      We sent a 6-digit code to <strong>{{ verificationEmail }}</strong>. Enter it below to continue.
                    </p>
                    <mat-form-field appearance="outline" class="verification-code-field">
                      <mat-label>Verification code</mat-label>
                      <input
                        matInput
                        [formControl]="verificationCodeControl"
                        inputmode="numeric"
                        autocomplete="one-time-code"
                        maxlength="6"
                        (keyup.enter)="continuePastAccountSetup()">
                    </mat-form-field>
                    <button mat-button type="button" color="primary" class="btn-resend" (click)="requestNewCode()" [disabled]="isCheckingCode">
                      Change email or send a new code
                    </button>
                  </div>

                  <p class="email-verification-error" *ngIf="verificationError">{{ verificationError }}</p>

                  <p class="email-verified" *ngIf="emailVerified">
                    <mat-icon>verified</mat-icon>
                    <span>Email verified: {{ verificationEmail }}</span>
                  </p>
                </div>

                <div class="step-actions">
                  <button mat-button matStepperPrevious class="btn-back" [disabled]="isLoading">
                    <mat-icon>arrow_back</mat-icon>
                    Back
                  </button>
                  <button mat-flat-button color="primary" class="btn-next" [disabled]="!canContinuePastAccountSetup" (click)="continuePastAccountSetup()">
                    {{ accountStepActionLabel }}
                    <mat-icon>arrow_forward</mat-icon>
                  </button>
                </div>
              </div>
            </mat-step>

            <!-- Step 3: Branding (Optional) -->
            <mat-step [editable]="true" [optional]="true">
              <ng-template matStepLabel></ng-template>
              <div class="step-form">
                <div class="form-header">
                  <h2 class="form-title">Customize Your Branding</h2>
                  <p class="form-description">Add your logo and icon to personalize your portal. You can always do this later in settings.</p>
                </div>

                <app-branding-info-form></app-branding-info-form>

                <div class="step-actions">
                  <button mat-button matStepperPrevious class="btn-back" [disabled]="isLoading">
                    <mat-icon>arrow_back</mat-icon>
                    Back
                  </button>
                  <div class="action-group">
                    <button mat-stroked-button color="primary" class="btn-skip" (click)="skipBrandingAndContinue()">
                      Skip Branding
                    </button>
                    <button mat-flat-button color="primary" matStepperNext class="btn-next">
                      Continue to Payment
                      <mat-icon>arrow_forward</mat-icon>
                    </button>
                  </div>
                </div>
              </div>
            </mat-step>

            <!-- Step 4: Payment -->
            <mat-step [editable]="true" [optional]="!requiresPaymentMethod">
              <ng-template matStepLabel></ng-template>
              <div class="step-form">
                <div class="form-header">
                  <h2 class="form-title">{{ requiresPaymentMethod ? 'Add Payment Method' : 'Add Payment Method (Optional)' }}</h2>
                  <p class="form-description">
                    {{ requiresPaymentMethod
                      ? 'Society and Growth require a card on file. You still get a 90-day free trial and will not be charged today.'
                      : 'Add your payment details to ensure uninterrupted service after your 90-day trial. You will not be charged today.' }}
                  </p>
                </div>

                <app-payment-info-form
                  [billingEmail]="getBillingEmail()"
                  (paymentMethodCreated)="onPaymentMethodCreated($event)"
                  (paymentSkipped)="onPaymentSkipped()">
                </app-payment-info-form>

                <div class="step-actions">
                  <button mat-button matStepperPrevious class="btn-back" [disabled]="isLoading">
                    <mat-icon>arrow_back</mat-icon>
                    Back
                  </button>
                  <div class="action-group">
                    <button
                      *ngIf="!requiresPaymentMethod"
                      mat-stroked-button
                      color="primary"
                      class="btn-skip"
                      (click)="onPaymentSkipped()"
                      [disabled]="isLoading">
                      <mat-icon *ngIf="isLoading && activeSubmitAction === 'skipPayment'" class="button-spin">autorenew</mat-icon>
                      <span>{{ isLoading && activeSubmitAction === 'skipPayment' ? 'Creating Account...' : 'Skip for Now' }}</span>
                    </button>
                    <button mat-flat-button color="primary" (click)="createAccountFromPaymentStep()" class="btn-submit" [disabled]="isLoading">
                      <mat-icon *ngIf="isLoading && activeSubmitAction === 'createAccount'" class="button-spin">autorenew</mat-icon>
                      <mat-icon *ngIf="!(isLoading && activeSubmitAction === 'createAccount')">check_circle</mat-icon>
                      <span>{{ isLoading && activeSubmitAction === 'createAccount' ? 'Creating Account...' : 'Create Account' }}</span>
                    </button>
                  </div>
                </div>

                <div class="submit-feedback" *ngIf="isLoading" aria-live="polite">
                  <mat-icon class="submit-feedback-icon">autorenew</mat-icon>
                  <div class="submit-feedback-copy">
                    <h4>Creating your organization</h4>
                    <p>Saving your organization and administrator account. This usually takes a few seconds.</p>
                  </div>
                </div>
              </div>
            </mat-step>
          </mat-vertical-stepper>
        </div>
      </div>
    </div>
  </div>
</div>
